import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@clerk/clerk-react';
import AdminAPI from '../../utils/adminApi';

const CourseManagementContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2rem;
  margin: 0;
  font-weight: 700;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  }
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const CourseCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: rgba(102, 126, 234, 0.4);
  }
`;

const CourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CourseTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #2ecc71, #27ae60)' 
    : 'linear-gradient(135deg, #e74c3c, #c0392b)'};
  color: white;
`;

const CourseInfo = styled.div`
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
`;

const CourseDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CourseActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.edit {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    border: 1px solid rgba(102, 126, 234, 0.2);
    
    &:hover {
      background: rgba(102, 126, 234, 0.2);
    }
  }
  
  &.bookings {
    background: rgba(155, 89, 182, 0.1);
    color: #9b59b6;
    border: 1px solid rgba(155, 89, 182, 0.2);
    
    &:hover {
      background: rgba(155, 89, 182, 0.2);
    }
  }
  
  &.toggle {
    background: ${props => props.active 
      ? 'rgba(231, 76, 60, 0.1)' 
      : 'rgba(46, 204, 113, 0.1)'};
    color: ${props => props.active ? '#e74c3c' : '#2ecc71'};
    border: 1px solid ${props => props.active 
      ? 'rgba(231, 76, 60, 0.2)' 
      : 'rgba(46, 204, 113, 0.2)'};
    
    &:hover {
      background: ${props => props.active 
        ? 'rgba(231, 76, 60, 0.2)' 
        : 'rgba(46, 204, 113, 0.2)'};
    }
  }
  
  &.delete {
    background: rgba(231, 76, 60, 0.1);
    color: #e74c3c;
    border: 1px solid rgba(231, 76, 60, 0.2);
    
    &:hover {
      background: rgba(231, 76, 60, 0.2);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 1rem;
  color: #e74c3c;
  text-align: center;
  margin: 1rem 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.textLight};
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.2rem;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  color: #e74c3c;
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
`;

const ModalMessage = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.cancel {
    background: rgba(108, 117, 125, 0.1);
    color: #6c757d;
    border: 1px solid rgba(108, 117, 125, 0.2);
    
    &:hover {
      background: rgba(108, 117, 125, 0.2);
    }
  }
  
  &.delete {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(231, 76, 60, 0.3);
    }
  }
`;

const CourseManagement = ({ onEditCourse, onCreateCourse, onBack, onViewBookings }) => {
  const { getToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, course: null });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }
      const response = await AdminAPI.getCourses(token);
      setCourses(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (courseId, currentStatus) => {
    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }
      await AdminAPI.toggleCourseActive(courseId, token);
      setCourses(courses.map(course => 
        course.id === courseId 
          ? { ...course, is_active: !currentStatus }
          : course
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const course = courses.find(c => c.id === courseId);
    setDeleteModal({ isOpen: true, course });
  };

  const confirmDeleteCourse = async () => {
    if (!deleteModal.course) return;

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }
      
      await AdminAPI.deleteCourse(deleteModal.course.id, token);
      setCourses(courses.filter(course => course.id !== deleteModal.course.id));
      setDeleteModal({ isOpen: false, course: null });
    } catch (err) {
      // Spezifische Fehlermeldungen basierend auf dem Fehler
      if (err.message.includes('COURSE_HAS_BOOKINGS')) {
        setError('Dieser Kurs kann nicht gelöscht werden, da noch Buchungen vorhanden sind. Bitte löschen Sie zuerst alle Buchungen für diesen Kurs.');
      } else if (err.message.includes('COURSE_NOT_FOUND')) {
        setError('Der Kurs wurde nicht gefunden oder wurde bereits gelöscht.');
      } else {
        setError(err.message || 'Fehler beim Löschen des Kurses');
      }
      setDeleteModal({ isOpen: false, course: null });
    }
  };

  const cancelDeleteCourse = () => {
    setDeleteModal({ isOpen: false, course: null });
  };

  const handleEditCourse = (courseId) => {
    if (onEditCourse) {
      onEditCourse(courseId);
    }
  };

  const handleCreateCourse = () => {
    if (onCreateCourse) {
      onCreateCourse();
    }
  };

  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <CourseManagementContainer>
        <LoadingSpinner>Kurse werden geladen...</LoadingSpinner>
      </CourseManagementContainer>
    );
  }

  return (
    <CourseManagementContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            ← Zurück zum Dashboard
          </BackButton>
          <Title>Kursverwaltung</Title>
        </HeaderLeft>
        <AddButton onClick={handleCreateCourse}>+ Neuer Kurs</AddButton>
      </Header>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {courses.length === 0 ? (
        <EmptyState>
          <h3>Keine Kurse vorhanden</h3>
          <p>Erstellen Sie Ihren ersten Kurs, um zu beginnen.</p>
        </EmptyState>
      ) : (
        <CourseGrid>
          <AnimatePresence>
            {courses.map((course, index) => (
              <CourseCard
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <CourseHeader>
                  <CourseTitle>{course.title}</CourseTitle>
                  <StatusBadge active={course.is_active}>
                    {course.is_active ? 'Aktiv' : 'Inaktiv'}
                  </StatusBadge>
                </CourseHeader>

                <CourseDescription>
                  {course.description || 'Keine Beschreibung verfügbar'}
                </CourseDescription>

                <CourseInfo>
                  <InfoRow>
                    <InfoLabel>Preis:</InfoLabel>
                    <InfoValue>{formatPrice(course.price, course.currency)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Kategorie:</InfoLabel>
                    <InfoValue>{course.category || 'Nicht zugewiesen'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Buchungen:</InfoLabel>
                    <InfoValue>{course.current_bookings} / {course.max_bookings}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Erstellt:</InfoLabel>
                    <InfoValue>{formatDate(course.created_at)}</InfoValue>
                  </InfoRow>
                </CourseInfo>

                <CourseActions>
                  <ActionButton 
                    className="edit"
                    onClick={() => handleEditCourse(course.id)}
                  >
                    Bearbeiten
                  </ActionButton>
                  <ActionButton 
                    className="bookings"
                    onClick={() => onViewBookings(course.id)}
                  >
                    Buchungen
                  </ActionButton>
                  <ActionButton 
                    className="toggle"
                    active={course.is_active}
                    onClick={() => handleToggleActive(course.id, course.is_active)}
                  >
                    {course.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </ActionButton>
                  <ActionButton 
                    className="delete"
                    onClick={() => handleDeleteCourse(course.id)}
                  >
                    Löschen
                  </ActionButton>
                </CourseActions>
              </CourseCard>
            ))}
          </AnimatePresence>
        </CourseGrid>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={cancelDeleteCourse}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>⚠️ Kurs löschen</ModalTitle>
                <ModalMessage>
                  Sind Sie sicher, dass Sie den Kurs <strong>"{deleteModal.course?.title}"</strong> löschen möchten?
                  <br />
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </ModalMessage>
              </ModalHeader>
              
              <ModalActions>
                <ModalButton className="cancel" onClick={cancelDeleteCourse}>
                  Abbrechen
                </ModalButton>
                <ModalButton className="delete" onClick={confirmDeleteCourse}>
                  Löschen
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </CourseManagementContainer>
  );
};

export default CourseManagement;
